/**
 * Sandwich page-break grouping for journey forms (ADR 0005).
 * Page-break rows are delimiters only; never appear as steps.
 * If a sandwich is illegal or empty, fall back to sequential for that sandwich.
 */

export const JOURNEY_PAGE_BREAK_QUESTION_TYPE = "JOURNEY-PAGE-BREAK";

export type JourneyPageBreakStepKind = "SEQUENTIAL" | "GROUPED";

export type JourneyPageBreakWarningReason =
  | "EMPTY-SANDWICH"
  | "CONDITIONAL-INTERIOR"
  | "PAGE-BREAK-IN-CONDITIONAL-GROUP"
  | "REPEATABLE-INTERIOR";

export type JourneyPageBreakGroupingQuestion = {
  questionId: string;
  questionType: string;
  /** Classic FormQuestions.ConditionalGroupId when available (admin / cache). */
  conditionalGroupId?: number | null;
  /** Option-level ConditionalGroupId values when available. */
  optionConditionalGroupIds?: Array<number | null | undefined>;
  /** Cache Repeatability; non-zero means competency/goal expand. */
  repeatability?: number | null;
  /** Journey wire proxy when ConditionalGroupId is not on the DTO. */
  isConditionallyTriggered?: boolean;
  /** Journey wire: sequenceMap.options present (branching source). */
  hasConditionalOptionBranches?: boolean;
};

export type JourneyPageBreakStep = {
  questionIds: string[];
  kind: JourneyPageBreakStepKind;
};

export type JourneyPageBreakWarning = {
  pageBreakQuestionId: string;
  interiorQuestionId?: string;
  reason: JourneyPageBreakWarningReason;
};

export type JourneyPageBreakGroupingResult = {
  steps: JourneyPageBreakStep[];
  warnings: JourneyPageBreakWarning[];
};

export function isJourneyPageBreakQuestionType(
  questionType: string | null | undefined
): boolean {
  return questionType === JOURNEY_PAGE_BREAK_QUESTION_TYPE;
}

function isRepeatable(question: JourneyPageBreakGroupingQuestion): boolean {
  return (
    question.repeatability != null &&
    question.repeatability !== 0
  );
}

function isConditionalInterior(
  question: JourneyPageBreakGroupingQuestion
): boolean {
  if (question.conditionalGroupId != null) {
    return true;
  }
  if (question.isConditionallyTriggered === true) {
    return true;
  }
  if (question.hasConditionalOptionBranches === true) {
    return true;
  }
  if (
    question.optionConditionalGroupIds != null &&
    question.optionConditionalGroupIds.some((id) => id != null)
  ) {
    return true;
  }
  return false;
}

/** Page-break itself sits in / is reached via a conditional group (admin id or journey proxy). */
function isPageBreakInConditionalGroup(
  question: JourneyPageBreakGroupingQuestion
): boolean {
  return (
    question.conditionalGroupId != null ||
    question.isConditionallyTriggered === true
  );
}

function pushSequentialSteps(
  steps: JourneyPageBreakStep[],
  questionIds: string[]
): void {
  questionIds.forEach((questionId) => {
    steps.push({ questionIds: [questionId], kind: "SEQUENTIAL" });
  });
}

/**
 * Build journey steps from an ordered question list using sandwich page-breaks.
 * Caller must pass questions in QuestionSequence / form array order.
 */
export function buildJourneyPageBreakSteps(
  questions: JourneyPageBreakGroupingQuestion[]
): JourneyPageBreakGroupingResult {
  const steps: JourneyPageBreakStep[] = [];
  const warnings: JourneyPageBreakWarning[] = [];

  if (!questions || questions.length === 0) {
    return { steps, warnings };
  }

  const breakIndices: number[] = [];
  questions.forEach((question, index) => {
    if (isJourneyPageBreakQuestionType(question.questionType)) {
      breakIndices.push(index);
      if (isPageBreakInConditionalGroup(question)) {
        warnings.push({
          pageBreakQuestionId: question.questionId,
          reason: "PAGE-BREAK-IN-CONDITIONAL-GROUP",
        });
      }
    }
  });

  if (breakIndices.length === 0) {
    questions.forEach((question) => {
      if (!isJourneyPageBreakQuestionType(question.questionType)) {
        steps.push({
          questionIds: [question.questionId],
          kind: "SEQUENTIAL",
        });
      }
    });
    return { steps, warnings };
  }

  // Before the first page-break: each non-break is its own step.
  for (let i = 0; i < breakIndices[0]; i++) {
    const question = questions[i];
    if (!isJourneyPageBreakQuestionType(question.questionType)) {
      steps.push({
        questionIds: [question.questionId],
        kind: "SEQUENTIAL",
      });
    }
  }

  // Strictly between consecutive page-breaks: sandwich (or sequential fallback).
  for (let b = 0; b < breakIndices.length - 1; b++) {
    const startExclusive = breakIndices[b];
    const endExclusive = breakIndices[b + 1];
    const leftBreak = questions[startExclusive];
    const interior: JourneyPageBreakGroupingQuestion[] = [];

    for (let i = startExclusive + 1; i < endExclusive; i++) {
      const question = questions[i];
      if (!isJourneyPageBreakQuestionType(question.questionType)) {
        interior.push(question);
      }
    }

    if (interior.length === 0) {
      warnings.push({
        pageBreakQuestionId: leftBreak.questionId,
        reason: "EMPTY-SANDWICH",
      });
      continue;
    }

    const illegalReasons: JourneyPageBreakWarning[] = [];
    interior.forEach((question) => {
      if (isConditionalInterior(question)) {
        illegalReasons.push({
          pageBreakQuestionId: leftBreak.questionId,
          interiorQuestionId: question.questionId,
          reason: "CONDITIONAL-INTERIOR",
        });
      }
      if (isRepeatable(question)) {
        illegalReasons.push({
          pageBreakQuestionId: leftBreak.questionId,
          interiorQuestionId: question.questionId,
          reason: "REPEATABLE-INTERIOR",
        });
      }
    });

    // Either delimiting page-break in a conditional group already warned; treat sandwich sequential.
    const rightBreak = questions[endExclusive];
    const breakDelimiterIllegal =
      isPageBreakInConditionalGroup(leftBreak) ||
      isPageBreakInConditionalGroup(rightBreak);

    if (illegalReasons.length > 0 || breakDelimiterIllegal) {
      warnings.push(...illegalReasons);
      pushSequentialSteps(
        steps,
        interior.map((q) => q.questionId)
      );
      continue;
    }

    steps.push({
      questionIds: interior.map((q) => q.questionId),
      kind: "GROUPED",
    });
  }

  // After the last page-break: each non-break is its own step.
  const afterLast = breakIndices[breakIndices.length - 1] + 1;
  for (let i = afterLast; i < questions.length; i++) {
    const question = questions[i];
    if (!isJourneyPageBreakQuestionType(question.questionType)) {
      steps.push({
        questionIds: [question.questionId],
        kind: "SEQUENTIAL",
      });
    }
  }

  return { steps, warnings };
}

/** Admin badge: SEQUENTIAL or GROUPED for a non-break question, or null if unknown / page-break. */
export function getJourneyQuestionGroupingKind(
  result: JourneyPageBreakGroupingResult,
  questionId: string
): JourneyPageBreakStepKind | null {
  const step = result.steps.find((s) => s.questionIds.includes(questionId));
  return step ? step.kind : null;
}
